/**
 * EventsTab — CFTC-regulated event / prediction contracts (Kalshi + Polymarket US).
 *
 * Full surface: browse markets, live orderbook, a four-way order ticket
 * (BUY/SELL x YES/NO) with true prediction-market P&L, and a portfolio panel
 * with cancel. BYOK: orders carry the user's own venue key from the vault, or
 * they are refused -- there is no platform account. Prices are implied
 * probability (a 62-cent YES = 62%).
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, HttpError } from '@/lib/api';
import { getUnlockedEventVenue, redirectToVault, type EventVenueName } from '@/lib/vault';
import { useEventStream } from '@/hooks/useEventStream';
import { ORG, GRN, RED, MUT, FONT } from '@/theme';

interface EventMarket {
  venue: string; ticker: string; title: string;
  yesBid: number | null; yesAsk: number | null;
  noBid: number | null; noAsk: number | null;
  last: number | null; volume: number | null;
  closeTime: string | null; status: string;
  category?: string | null; eventTicker?: string | null;
}
interface OBLevel { price: number; count: number; }
interface Orderbook { venue: string; ticker: string; yes: OBLevel[]; no: OBLevel[]; }
interface HistoryPoint { ts: string; yesPrice: number; }
interface Portfolio {
  venue: string;
  balance: { cashDollars: number | null };
  positions: Array<{ ticker: string; side: string; count: number; avgPrice: number | null; marketValue?: number | null }>;
  orders: Array<{ orderId: string; ticker: string; side: string; count: number; remainingCount: number; price: number | null; status: string }>;
  equityDollars?: number | null;
}

type SortKey = 'edge' | 'volume' | 'close' | 'alpha';

type Action = 'buy' | 'sell';
type Side = 'yes' | 'no';

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v)) ? v : NaN;
const pct = (p: number | null): string => Number.isFinite(num(p)) ? `${(num(p) * 100).toFixed(0)}%` : '—';
const money = (d: number): string => Number.isFinite(d) ? `$${d.toFixed(2)}` : '—';
// Kalshi fee (dollars): ceil(0.07 * count * p * (1-p)) to the cent.
const feeOf = (p: number, c: number): number => (p > 0 && p < 1 && c > 0) ? Math.ceil(0.07 * c * p * (1 - p) * 100) / 100 : 0;
// Guaranteed edge ($/contract-pair) from buying YES+NO on the same market: one
// side settles at $1, so profit = 1 - yesAsk - noAsk - fees. Positive == arb.
const arbEdge = (m: { yesAsk: number | null; noAsk: number | null }): number => {
  const ya = num(m.yesAsk), na = num(m.noAsk);
  if (!Number.isFinite(ya) || !Number.isFinite(na)) return NaN;
  return 1 - ya - na - feeOf(ya, 1) - feeOf(na, 1);
};

export function EventsTab() {
  const qc = useQueryClient();
  const [venue, setVenue] = useState<EventVenueName>('kalshi');
  const [selected, setSelected] = useState<EventMarket | null>(null);
  const [action, setAction] = useState<Action>('buy');
  const [side, setSide] = useState<Side>('yes');
  const [count, setCount] = useState<number | ''>(1);
  const [cents, setCents] = useState<number | ''>(50);
  const [placing, setPlacing] = useState(false);
  const [arbOnly, setArbOnly] = useState(false);
  const [probEst, setProbEst] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('volume');
  const [category, setCategory] = useState('');

  const creds = getUnlockedEventVenue(venue);
  const vaultOk = creds !== null;
  const mode = creds?.mode ?? 'prod';
  // The RSA private key is a multi-line PEM; newlines are ILLEGAL in an HTTP
  // header value (fetch throws), so collapse them here. The server's pemToDer
  // strips all whitespace + the BEGIN/END markers, so a single-line PEM still
  // reconstructs the exact DER. The order POST sends the key in the JSON body
  // instead, where newlines are fine.
  const eventHeaders = creds
    ? { 'x-event-key': creds.apiKeyId, 'x-event-secret': creds.privateKey.replace(/[\r\n]+/g, ''), 'x-event-mode': creds.mode }
    : undefined;

  const marketsQ = useQuery<{ venue: string; markets: EventMarket[]; stale?: boolean; stale_as_of?: string }>({
    queryKey: ['events-markets', venue, mode, vaultOk],
    // Send the vault headers so the server can SIGN the upstream read. Without
    // them the signing work was inert here: Kalshi meters unsigned reads per
    // shared Cloudflare IP, which is the 429 that took this tab down.
    queryFn:  () => api(`/api/events/markets?venue=${venue}&status=open&limit=400&mode=${mode}`, { headers: eventHeaders }),
    staleTime: 15_000, refetchInterval: 20_000, retry: false,
  });
  const pendingAccess = marketsQ.isError && /PENDING_API_ACCESS/.test((marketsQ.error as Error)?.message ?? '');

  const obQ = useQuery<Orderbook>({
    queryKey: ['events-ob', venue, selected?.ticker, mode, vaultOk],
    // Signed too: at a 1.5s poll this is the heaviest upstream caller by far.
    queryFn:  () => api(`/api/events/orderbook/${venue}/${encodeURIComponent(selected!.ticker)}?mode=${mode}`, { headers: eventHeaders }),
    enabled:  !!selected, staleTime: 1_000, refetchInterval: 1_500, retry: false,
  });

  // Implied-probability trajectory (public trades feed). Degrades to no chart on
  // error/empty; never a fabricated flat line.
  const histQ = useQuery<{ points: HistoryPoint[] }>({
    queryKey: ['events-history', venue, selected?.ticker, mode],
    queryFn:  () => api(`/api/events/history/${venue}/${encodeURIComponent(selected!.ticker)}?mode=${mode}`),
    enabled:  !!selected, staleTime: 20_000, refetchInterval: 30_000, retry: false,
  });

  const pfQ = useQuery<Portfolio>({
    queryKey: ['events-portfolio', venue, mode],
    queryFn:  () => api(`/api/events/portfolio?venue=${venue}`, { headers: eventHeaders }),
    enabled:  vaultOk, staleTime: 8_000, refetchInterval: 12_000, retry: false,
  });

  // Live Kalshi orderbook + fills relay. ADDITIVE: when the upstream relay is
  // healthy, `stream.book` refreshes faster than the 1.5s REST poll; when it is
  // not (locked vault, non-Kalshi venue, relay down, or seq gap), `stream.book`
  // is null and the REST book below is shown unchanged. A fill on our account
  // nudges the portfolio to refetch immediately instead of waiting 12s.
  const stream = useEventStream({
    venue,
    ticker: selected?.ticker ?? null,
    mode,
    creds: creds ? { apiKeyId: creds.apiKeyId, privateKey: creds.privateKey } : null,
    enabled: vaultOk,
    onFill: () => { void qc.invalidateQueries({ queryKey: ['events-portfolio', venue, mode] }); },
  });
  // REST is authoritative; the live book only overrides it while delivering.
  const book = stream.book ?? obQ.data ?? null;
  const bookIsLive = stream.book !== null && stream.live;

  // ---- P&L for the current ticket ----------------------------------------
  const pnl = useMemo(() => {
    const c = num(count); const p = num(cents) / 100;
    if (!Number.isFinite(c) || c <= 0 || !(p > 0 && p < 1)) return null;
    const premium = p * c;               // paid (buy) or received (sell)
    const other   = (1 - p) * c;
    const fee     = feeOf(p, c);
    return action === 'buy'
      ? { label: 'Cost', principal: premium + fee, maxWin: other - fee, maxLoss: premium + fee, fee }
      : { label: 'Proceeds', principal: premium - fee, maxWin: premium - fee, maxLoss: other + fee, fee };
  }, [action, count, cents]);

  // Prefill the price from the book when a side/action is chosen.
  const pickSide = (a: Action, s: Side, mkt: EventMarket | null = selected) => {
    setAction(a); setSide(s);
    if (!mkt) return;
    const px = s === 'yes'
      ? (a === 'buy' ? mkt.yesAsk : mkt.yesBid)
      : (a === 'buy' ? mkt.noAsk  : mkt.noBid);
    if (Number.isFinite(num(px))) setCents(Math.round(num(px) * 100));
  };

  const place = async () => {
    if (placing) return;
    if (!creds) { toast.error('Unlock your event-venue vault to trade.'); return; }
    if (!selected) { toast.error('Select a market first.'); return; }
    // The stale banner says "trading and arb are blocked" -- make that true for
    // the ORDER TICKET too, not just the arb button. The ticket's price is
    // prefilled from these same quotes, so submitting against a stale snapshot
    // sends a limit derived from prices that no longer exist.
    if (marketsQ.data?.stale) { toast.error('Prices are a stale snapshot (venue rate-limited). Trading is blocked until live quotes return.'); return; }
    const c = num(count), ct = num(cents);
    if (!Number.isFinite(c) || c <= 0) { toast.error('Count required'); return; }
    if (!Number.isFinite(ct) || ct < 1 || ct > 99) { toast.error('Price must be 1-99 cents'); return; }
    // Live money gets a modal confirmation; demo does not.
    if (mode === 'prod') {
      const ok = window.confirm(
        `*** LIVE ${venue.toUpperCase()} -- REAL MONEY ***\n\n` +
        `${action.toUpperCase()} ${c} ${side.toUpperCase()} @ ${ct}c\n${selected.title}\n\n` +
        (pnl ? `${pnl.label}: ${money(pnl.principal)} | max win ${money(pnl.maxWin)} | max loss ${money(pnl.maxLoss)} | fee ~${money(pnl.fee)}\n\n` : '') +
        `Place this order?`);
      if (!ok) return;
    }
    setPlacing(true);
    // No client-generated order id: the server derives a deterministic one from
    // the order identity so a retry cannot double-fill (a random id here would
    // defeat that dedup). See eventRoutes deriveEventClientOrderId.
    try {
      await api('/api/events/orders', {
        method: 'POST',
        body: JSON.stringify({
          venue, ticker: selected.ticker, action, side, count: c, limitPrice: ct / 100,
          event_api_key: creds.apiKeyId, event_api_secret: creds.privateKey, event_api_mode: creds.mode,
        }),
      });
      toast.success(`${action.toUpperCase()} ${c} ${side.toUpperCase()} ${selected.ticker} @ ${ct}c ${mode === 'demo' ? '(demo)' : ''}`);
      void qc.invalidateQueries({ queryKey: ['events-portfolio', venue, mode] });
      void qc.invalidateQueries({ queryKey: ['events-ob', venue, selected.ticker, mode] });
    } catch (e) {
      const err = e as HttpError;
      if (err.status === 409 || err.code === 'DUPLICATE_ORDER_SUPPRESSED') {
        toast('Duplicate suppressed — NO new order was placed.', { icon: '⚠', duration: 7000 });
      } else if (typeof err.status !== 'number') {
        toast('Could not confirm — the connection failed before we heard back. Check your resting orders before retrying.', { icon: '⚠', duration: 12000 });
      } else {
        toast.error(`${err.message}${err.code ? ` (${err.code})` : ''}`);
      }
    } finally { setPlacing(false); }
  };

  const cancel = async (orderId: string) => {
    if (!eventHeaders) { toast.error('Unlock your vault to cancel.'); return; }
    try {
      await api(`/api/events/orders/${venue}/${encodeURIComponent(orderId)}`, { method: 'DELETE', headers: eventHeaders });
      toast.success('Cancel sent');
      void qc.invalidateQueries({ queryKey: ['events-portfolio', venue, mode] });
    } catch (e) { toast.error((e as Error).message); }
  };

  // Top-of-book fillable pairs for an arb on `mkt`. On Kalshi, buying YES lifts
  // NO bids and buying NO lifts YES bids, so the size available to BUY YES at
  // yesAsk is the best NO-bid size, and to BUY NO at noAsk is the best YES-bid
  // size. Only the TOP level preserves the quoted edge (deeper levels fill
  // worse), so we cap to it. Returns null if the book can't be read, in which
  // case the caller proceeds unsized -- the auto-flatten below still protects a
  // partial.
  const arbDepth = async (mkt: EventMarket): Promise<{ maxPairs: number; corroborated: boolean } | null> => {
    try {
      const ob = await api<Orderbook>(`/api/events/orderbook/${venue}/${encodeURIComponent(mkt.ticker)}?mode=${mode}`);
      const best = (lv: OBLevel[] | undefined) =>
        (Array.isArray(lv) ? lv : []).reduce<OBLevel | null>((b, l) => (l && num(l.price) > (b ? num(b.price) : -1) ? l : b), null);
      const noBid = best(ob.no), yesBid = best(ob.yes);
      if (!noBid || !yesBid) return { maxPairs: 0, corroborated: false };
      // The best NO bid implies the YES ask (1 - price) and vice-versa. If the
      // book doesn't corroborate the quoted asks within a cent the quote is
      // stale -- treat depth as unknown rather than trust it.
      const ya = num(mkt.yesAsk), na = num(mkt.noAsk);
      const corroborated = Math.abs((1 - num(noBid.price)) - ya) <= 0.011 && Math.abs((1 - num(yesBid.price)) - na) <= 0.011;
      return { maxPairs: Math.max(0, Math.min(num(noBid.count), num(yesBid.count))), corroborated };
    } catch { return null; }
  };

  // Flatten a naked leg left by an unequal arb fill. Sells the over-filled side
  // at its bid so the position returns to a matched hedge (or flat). This
  // realizes only the spread -- far cheaper than carrying directional exposure
  // from a tool sold as riskless. Loud on success AND on failure (a failed
  // flatten must never look handled).
  const flattenNaked = async (mkt: EventMarket, side: Side, qty: number, bidPx: number, locked: number) => {
    // A limit SELL at the bid fills against resting bids at/above it -- a
    // marketable exit, not a dump. If there's no bid, fall to 1c, which still
    // only fills against whatever bid exists.
    const px = Number.isFinite(bidPx) && bidPx > 0 ? bidPx : 0.01;
    try {
      await api('/api/events/orders', {
        method: 'POST',
        body: JSON.stringify({
          venue, ticker: mkt.ticker, action: 'sell', side, count: qty, limitPrice: px,
          event_api_key: creds!.apiKeyId, event_api_secret: creds!.privateKey, event_api_mode: creds!.mode,
        }),
      });
      toast(`ARB partial auto-flattened: sold ${qty} naked ${side.toUpperCase()} on ${mkt.ticker}.` +
        (locked > 0 ? ` ${locked} pair(s) remain hedged.` : ' Position is flat.'), { icon: '↩', duration: 12000 });
      void qc.invalidateQueries({ queryKey: ['events-portfolio', venue, mode] });
    } catch (e) {
      const err = e as HttpError;
      toast(`AUTO-FLATTEN FAILED (${err.message || 'error'}). You are NAKED ${qty} ${side.toUpperCase()} on ${mkt.ticker} — unwind manually NOW.`,
        { icon: '⚠', duration: 20000 });
    }
  };

  // One-click arbitrage: buy YES + buy NO on the same market. One side settles at
  // $1, so if yesAsk + noAsk + fees < $1 the pair locks a guaranteed profit. The
  // count is sized to top-of-book depth so it actually locks; both legs fire
  // together; and any imbalance from an unequal fill is auto-flattened so the
  // "riskless" promise holds even when only one side fills.
  const placeArb = async (mkt: EventMarket) => {
    if (placing) return;
    if (!creds) { toast.error('Unlock your vault to trade.'); return; }
    // NEVER arb off a stale snapshot. When the venue rate-limits us the server
    // serves the last good prices (clearly flagged) so the screen stays useful --
    // but an "edge" computed from stale quotes is fiction, and acting on it is
    // how you buy both sides of a spread that no longer exists.
    if (marketsQ.data?.stale) {
      toast.error('Prices are a stale snapshot (venue rate-limited). Arb is disabled until live quotes return.');
      return;
    }
    const ya = num(mkt.yesAsk), na = num(mkt.noAsk);
    const reqC = Math.max(1, Math.trunc(num(count) || 1));
    if (!Number.isFinite(ya) || !Number.isFinite(na)) { toast.error('No two-sided quote for an arb.'); return; }
    const edge = arbEdge(mkt);
    if (!(edge > 0)) { toast.error('No positive edge on this market right now.'); return; }

    // DEPTH-AWARE SIZING -- FAIL CLOSED. Previously a null depth (book read
    // failed / throttled) OR corroborated===false (the live book CONTRADICTS the
    // quoted ask, i.e. a stale quote) both fell through and fired the FULL
    // requested size unsized. The server bot fails closed on the same check;
    // two implementations of one control with opposite failure directions is
    // the vulnerability. If the book cannot confirm the edge, do not trade it.
    const depth = await arbDepth(mkt);
    if (!depth) { toast.error('Could not read the order book — arb blocked (cannot confirm the edge is takeable).'); return; }
    if (!depth.corroborated) { toast.error('The live book contradicts the quoted price (stale quote) — arb blocked.'); return; }
    if (depth.maxPairs < 1) { toast.error('No size at the locking price right now — the edge is not takeable.'); return; }
    const c = Math.min(reqC, depth.maxPairs);
    const sizedDown = c < reqC;

    if (mode === 'prod' && !window.confirm(
      `*** LIVE ARB PAIR -- REAL MONEY ***\n\n` +
      `BUY ${c} YES @ ${Math.round(ya * 100)}c  +  BUY ${c} NO @ ${Math.round(na * 100)}c\n${mkt.title}\n\n` +
      (sizedDown ? `Sized down from ${reqC} to ${c} — only ${c} pair(s) fill at the locking price right now.\n` : '') +
      `Guaranteed edge ~${money(edge * c)} net of fees, IF both legs fill. ` +
      `Any imbalance is auto-flattened. Place both?`)) return;
    setPlacing(true);
    try {
      const leg = (s: Side, px: number) => api<{ status?: string; remainingCount?: number }>('/api/events/orders', {
        method: 'POST',
        body: JSON.stringify({
          venue, ticker: mkt.ticker, action: 'buy', side: s, count: c, limitPrice: px,
          event_api_key: creds.apiKeyId, event_api_secret: creds.privateKey, event_api_mode: creds.mode,
        }),
      });
      const res = await Promise.allSettled([leg('yes', ya), leg('no', na)]);
      // ACCEPTED is not FILLED. Compute the ACTUAL filled quantity per leg from
      // remainingCount; the hedge is only the MATCHED amount (min of the two),
      // and any difference is a naked position to flatten.
      const filledOf = (r: PromiseSettledResult<{ status?: string; remainingCount?: number }>) =>
        r.status === 'fulfilled' ? Math.max(0, c - Number(r.value?.remainingCount ?? c)) : 0;
      const fy = filledOf(res[0]);
      const fn = filledOf(res[1]);
      // Capture WHY a leg failed. These reasons were computed and thrown away,
      // so a rejection was indistinguishable from an unfilled resting order.
      const rejected = res
        .map((r, i) => (r.status === 'rejected' ? `${i === 0 ? 'YES' : 'NO'}: ${String((r.reason as Error)?.message ?? r.reason).slice(0, 120)}` : null))
        .filter((x): x is string => x !== null);
      const locked = Math.min(fy, fn);
      const imbalance = fy - fn;   // >0 excess YES, <0 excess NO

      if (imbalance !== 0) {
        const side: Side = imbalance > 0 ? 'yes' : 'no';
        const exitPx = imbalance > 0 ? num(mkt.yesBid) : num(mkt.noBid);
        await flattenNaked(mkt, side, Math.abs(imbalance), exitPx, locked);
      } else if (locked > 0) {
        toast.success(`ARB filled: ${locked} pair(s) on ${mkt.ticker} — hedge locked`);
      } else if (rejected.length) {
        // A REJECTED leg is not a resting leg. Scoring it as "filled 0" and then
        // saying "both legs are resting, cancel them" told the trader to cancel
        // orders that do not exist -- and, worse, a leg that failed on TRANSPORT
        // may actually have reached the venue. Report the real outcome and say
        // plainly that it is unverified.
        toast(`ARB leg error on ${mkt.ticker}: ${rejected.join(' | ')}. ` +
          `Outcome UNVERIFIED - a leg may still have reached the venue. Check your resting orders and positions NOW.`,
          { icon: '⚠', duration: 20000 });
      } else {
        toast('ARB: both legs are RESTING, nothing filled yet. No hedge and no exposure — cancel them if the edge is gone.', { icon: '⚠', duration: 12000 });
      }
      void qc.invalidateQueries({ queryKey: ['events-portfolio', venue, mode] });
    } catch (e) {
      // try/finally with NO catch made any throw here an unhandled rejection:
      // the button silently re-enabled AFTER two live legs had been sent, with
      // nothing on screen. A money path must never fail silently.
      toast(`ARB ERROR on ${mkt.ticker}: ${(e as Error)?.message || e}. ` +
        `Outcome UNVERIFIED - check your resting orders and positions before retrying.`,
        { icon: '⚠', duration: 20000 });
      void qc.invalidateQueries({ queryKey: ['events-portfolio', venue, mode] });
    } finally { setPlacing(false); }
  };

  const markets = marketsQ.data?.markets ?? [];
  const categories = useMemo(
    () => Array.from(new Set(markets.map(m => m.category).filter((c): c is string => !!c))).sort(),
    [markets]);
  const vol = (m: EventMarket) => { const v = num(m.volume); return Number.isFinite(v) ? v : 0; };
  const edgeOf = (m: EventMarket) => { const e = arbEdge(m); return Number.isFinite(e) ? e : -Infinity; };
  // Discovery: category + text filter, then sort. Arb mode overrides sort with
  // descending guaranteed edge.
  const shown = useMemo(() => {
    let list = markets.slice();
    if (category) list = list.filter(m => m.category === category);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(m => m.title.toLowerCase().includes(q) || m.ticker.toLowerCase().includes(q));
    if (arbOnly) return list.filter(m => edgeOf(m) > 0).sort((a, b) => edgeOf(b) - edgeOf(a));
    return list.sort((a, b) =>
      sortBy === 'volume' ? vol(b) - vol(a)
      : sortBy === 'close' ? (a.closeTime ?? '~').localeCompare(b.closeTime ?? '~')
      : sortBy === 'edge'  ? edgeOf(b) - edgeOf(a)
      :                      a.title.localeCompare(b.title));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markets, category, search, arbOnly, sortBy]);

  // MULTI-OUTCOME EVENT ARB — the structural edge a single-market YES+NO pair
  // misses. In a mutually-exclusive event exactly one outcome's YES settles at
  // $1, so buying one YES of EVERY outcome costs Σ(yesAsk) and pays $1: a lock
  // when Σ(yesAsk) + fees < 1. Only surfaced when every outcome is quoted;
  // flagged "verify exhaustive" because a non-exhaustive event breaks the lock.
  const eventArbs = useMemo(() => {
    const groups = new Map<string, EventMarket[]>();
    for (const m of markets) {
      if (!m.eventTicker) continue;
      const g = groups.get(m.eventTicker) ?? [];
      g.push(m); groups.set(m.eventTicker, g);
    }
    const out: Array<{ event: string; n: number; sumAsk: number; edge: number }> = [];
    for (const [event, ms] of groups) {
      if (ms.length < 2) continue;
      const asks = ms.map(m => num(m.yesAsk));
      if (!asks.every(a => Number.isFinite(a) && a > 0 && a < 1)) continue;   // need every outcome quoted
      const sumAsk = asks.reduce((a, b) => a + b, 0);
      const fee = ms.reduce((a, m) => a + feeOf(num(m.yesAsk), 1), 0);
      const edge = 1 - sumAsk - fee;
      if (edge > 0) out.push({ event, n: ms.length, sumAsk, edge });
    }
    return out.sort((a, b) => b.edge - a.edge);
  }, [markets]);
  const ev: number | null = (() => {
    const pe = num(probEst) / 100, p = num(cents) / 100, c = num(count);
    if (!(pe >= 0 && pe <= 1) || !(p > 0 && p < 1) || !(c > 0)) return null;
    const winProb = side === 'yes' ? pe : 1 - pe;              // P(the side you hold wins)
    const perContract = action === 'buy' ? (winProb - p) : (p - winProb);
    return perContract * c - feeOf(p, c);
  })();

  return (
    <div style={{ fontFamily: FONT, fontSize: 11, display: 'flex', gap: 12, height: '100%', minHeight: 0 }}>
      {/* LEFT: venue toggle + market list */}
      <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {(['kalshi', 'polymarket_us'] as EventVenueName[]).map(v => (
            <button key={v} onClick={() => { setVenue(v); setSelected(null); }} style={{
              background: venue === v ? ORG : 'transparent', color: venue === v ? '#000' : '#888',
              border: '1px solid ' + (venue === v ? ORG : '#333'), fontFamily: FONT, fontSize: 10,
              padding: '2px 10px', borderRadius: 2, cursor: 'pointer', letterSpacing: 0.5,
            }}>{v === 'kalshi' ? 'KALSHI' : 'POLYMARKET US'}</button>
          ))}
          <span style={{
            fontSize: 9, padding: '1px 6px', borderRadius: 2, letterSpacing: 1,
            color: vaultOk ? (mode === 'demo' ? GRN : '#fff') : MUT,
            background: vaultOk && mode === 'prod' ? RED : 'transparent',
            border: '1px solid ' + (vaultOk ? (mode === 'demo' ? GRN : RED) : '#333'),
          }}>{vaultOk ? (mode === 'demo' ? 'DEMO' : 'LIVE') : 'NOT CONNECTED'}</span>
          <button onClick={() => setArbOnly(v => !v)} title="Show only YES+NO arbitrage (guaranteed edge net of fees)" style={{
            marginLeft: 'auto', background: arbOnly ? GRN : 'transparent', color: arbOnly ? '#000' : GRN,
            border: `1px solid ${GRN}`, fontFamily: FONT, fontSize: 9, padding: '2px 8px', borderRadius: 2, cursor: 'pointer', letterSpacing: 0.5,
          }}>ARB{arbOnly ? ' ●' : ''}</button>
        </div>

        {/* Discovery: search + category + sort over the fetched markets */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="search markets…"
            style={{ flex: 1, minWidth: 0, background: '#111', color: '#ddd', border: '1px solid #333', fontFamily: FONT, fontSize: 10, padding: '3px 6px', borderRadius: 2, outline: 'none' }} />
          {categories.length > 0 && (
            <select value={category} onChange={e => setCategory(e.target.value)} title="Category" style={selStyle}>
              <option value="">All</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <select value={sortBy} onChange={e => setSortBy(e.target.value as SortKey)} title="Sort" disabled={arbOnly} style={{ ...selStyle, opacity: arbOnly ? 0.5 : 1 }}>
            <option value="volume">Vol</option>
            <option value="close">Close</option>
            <option value="edge">Edge</option>
            <option value="alpha">A-Z</option>
          </select>
        </div>
        {!marketsQ.isLoading && !marketsQ.isError && (
          <div style={{ fontSize: 9, color: MUT, marginBottom: 3 }}>{shown.length} of {markets.length} markets{arbOnly ? ' · arb only' : ''}</div>
        )}

        {marketsQ.isLoading && <div style={{ color: MUT }}>Loading markets…</div>}
        {pendingAccess && (
          <div style={{ border: '1px solid #c08a3e', background: 'rgba(192,138,62,0.10)', padding: '6px 9px', color: '#c08a3e', fontSize: 10.5, lineHeight: 1.5 }}>
            <strong>POLYMARKET US — API ACCESS PENDING.</strong> Apply at support@polymarket.us; it goes live here with no further wiring. Kalshi works today.
          </div>
        )}
        {marketsQ.isError && !pendingAccess && <div style={{ color: RED }}>Markets unavailable — {(marketsQ.error as Error)?.message}</div>}
        {/* The venue throttled us and the server served the last good snapshot.
            Say so loudly: cached prices must never be mistaken for live ones. */}
        {marketsQ.data?.stale && (
          <div style={{ border: '1px solid #c08a3e', background: 'rgba(192,138,62,0.10)', padding: '5px 8px', marginBottom: 6, color: '#c08a3e', fontSize: 10, lineHeight: 1.5 }}>
            STALE SNAPSHOT — venue rate-limited. Prices from{' '}
            {marketsQ.data.stale_as_of ? new Date(marketsQ.data.stale_as_of).toLocaleTimeString() : 'earlier'}, not live. Trading and arb are blocked until live quotes return.
          </div>
        )}

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {/* Multi-outcome event locks — buy every outcome's YES for a $1 payout
              at a sub-$1 cost. Shown in arb mode; click to see the outcomes. */}
          {arbOnly && eventArbs.length > 0 && (
            <div style={{ border: '1px solid rgba(63,191,111,0.3)', borderRadius: 3, padding: '4px 6px', marginBottom: 6 }}>
              <div style={{ fontSize: 9, color: GRN, letterSpacing: 1, marginBottom: 3 }}>
                MULTI-OUTCOME LOCKS <span style={{ color: MUT }}>· verify outcomes are exhaustive</span>
              </div>
              {eventArbs.slice(0, 6).map(a => (
                <div key={a.event} onClick={() => setSearch(a.event)} title="Buy 1 YES of each outcome; click to filter to them"
                  style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, cursor: 'pointer', color: '#cfcfcf', padding: '1px 0' }}>
                  <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.event}</span>
                  <span>{a.n} outcomes · Σ{a.sumAsk.toFixed(2)} <span style={{ color: GRN, fontWeight: 'bold' }}>+{money(a.edge)}</span></span>
                </div>
              ))}
            </div>
          )}
          {arbOnly && shown.length === 0 && eventArbs.length === 0 && !marketsQ.isLoading && <div style={{ color: MUT, padding: '4px 6px' }}>No arbitrage right now.</div>}
          {shown.map(m => {
            const isSel = selected?.ticker === m.ticker;
            return (
              <div key={m.ticker} onClick={() => { setSelected(m); pickSide(action, side, m); }} style={{
                display: 'flex', justifyContent: 'space-between', gap: 8, padding: '4px 6px', cursor: 'pointer',
                borderBottom: '1px solid #161616', background: isSel ? 'rgba(255,153,0,0.10)' : 'transparent',
                borderLeft: '2px solid ' + (isSel ? ORG : 'transparent'),
              }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#e0e0e0' }} title={m.title}>{m.title}</span>
                <span style={{ color: GRN, minWidth: 42, textAlign: 'right' }}>Y {pct(m.yesAsk ?? m.last)}</span>
                <span style={{ color: RED, minWidth: 42, textAlign: 'right' }}>N {pct(m.noAsk)}</span>
                {arbOnly && <span style={{ color: GRN, minWidth: 48, textAlign: 'right', fontWeight: 'bold' }} title="guaranteed edge per contract pair, net of fees">+{money(arbEdge(m))}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT: orderbook + ticket + portfolio */}
      <div style={{ flex: '0 0 320px', display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
        {!vaultOk && (
          <div style={{ border: '1px solid #c08a3e', background: 'rgba(192,138,62,0.10)', padding: '6px 8px', color: '#c08a3e', fontSize: 10 }}>
            <strong>Not connected.</strong> <span onClick={redirectToVault} style={{ color: '#d4a45a', cursor: 'pointer', textDecoration: 'underline' }}>Unlock {venue === 'kalshi' ? 'Kalshi' : 'Polymarket US'}</span> to trade your own account.
          </div>
        )}

        {selected ? (
          <>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <div style={{ color: ORG, fontSize: 10, letterSpacing: 1 }}>{selected.ticker}</div>
                {/* Live pip: lit only when the WS relay is actually delivering a
                    book. When dark, the book below is the REST poll — still live
                    data, just at 1.5s cadence, never stale-rendered as fast. */}
                <span title={bookIsLive ? 'Live relay' : 'REST (relay idle)'} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: 8, letterSpacing: 1, color: bookIsLive ? GRN : MUT,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: bookIsLive ? GRN : '#555', display: 'inline-block' }} />
                  {bookIsLive ? 'LIVE' : 'REST'}
                </span>
              </div>
              <div style={{ color: '#e0e0e0', fontSize: 10.5, marginBottom: 4, lineHeight: 1.35 }}>{selected.title}</div>

              {/* Market detail: resolution timing + liquidity + status. You
                  should never trade a market blind to when it settles or how
                  thin it is. */}
              {(() => { const c = timeToClose(selected.closeTime); return (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 9, color: MUT, marginBottom: 6 }}>
                  <span>closes <span style={{ color: c.soon ? ORG : '#cfcfcf' }}>{c.label}</span></span>
                  {Number.isFinite(num(selected.volume)) && <span>vol <span style={{ color: '#cfcfcf' }}>{Math.round(num(selected.volume)).toLocaleString()}</span></span>}
                  {selected.category && <span style={{ color: '#cfcfcf' }}>{selected.category}</span>}
                  <span style={{ color: selected.status === 'open' ? GRN : ORG }}>{String(selected.status ?? '').toUpperCase()}</span>
                </div>
              ); })()}

              {/* Implied-probability trajectory (public trades). */}
              <ProbChart points={histQ.data?.points ?? []} live={Number.isFinite(num(selected.last)) ? num(selected.last) : null} loading={histQ.isLoading} />

              {/* One-click arb when this market shows a guaranteed edge. */}
              {edgeOf(selected) > 0 && (
                <button onClick={() => placeArb(selected)} disabled={placing || !vaultOk}
                  title="Buy YES + NO together for a guaranteed edge net of fees (fills both legs; partial fills are flagged)"
                  style={{ width: '100%', margin: '6px 0 2px', background: 'transparent', color: GRN, border: `1px solid ${GRN}`, fontFamily: FONT, fontSize: 10, fontWeight: 'bold', padding: '4px 0', borderRadius: 2, cursor: (placing || !vaultOk) ? 'not-allowed' : 'pointer', opacity: (placing || !vaultOk) ? 0.5 : 1, letterSpacing: 0.5 }}>
                  ⚡ ARB YES+NO · +{money(arbEdge(selected))}/pair
                </button>
              )}

              {/* Spread / mid + depth-weighted book. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: MUT, margin: '6px 0 3px' }}>
                <span>bid <span style={{ color: GRN }}>{pct(selected.yesBid)}</span></span>
                <span>mid {pct(Number.isFinite(num(selected.yesBid)) && Number.isFinite(num(selected.yesAsk)) ? (num(selected.yesBid) + num(selected.yesAsk)) / 2 : null)}</span>
                <span>ask <span style={{ color: RED }}>{pct(selected.yesAsk)}</span></span>
                <span>spr {Number.isFinite(num(selected.yesAsk)) && Number.isFinite(num(selected.yesBid)) ? `${Math.round((num(selected.yesAsk) - num(selected.yesBid)) * 100)}c` : '—'}</span>
              </div>
              <DepthBook book={book} />
              {!book && obQ.isError && <div style={{ color: RED, fontSize: 9 }}>book unavailable</div>}
            </div>

            {/* Order ticket */}
            <div style={{ borderTop: '1px solid #222', paddingTop: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 6 }}>
                {([['buy', 'yes'], ['sell', 'yes'], ['buy', 'no'], ['sell', 'no']] as Array<[Action, Side]>).map(([a, s]) => {
                  const on = action === a && side === s;
                  const clr = s === 'yes' ? GRN : RED;
                  return (
                    <button key={a + s} onClick={() => pickSide(a, s)} style={{
                      background: on ? clr : 'transparent', color: on ? '#000' : clr,
                      border: `1px solid ${clr}`, fontFamily: FONT, fontSize: 10, fontWeight: 'bold',
                      padding: '3px 0', borderRadius: 2, cursor: 'pointer', letterSpacing: 0.5,
                    }}>{a.toUpperCase()} {s.toUpperCase()}</button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <label style={{ flex: 1, color: MUT, fontSize: 9 }}>QTY
                  <input type="number" min={1} value={count} onChange={e => setCount(e.target.value === '' ? '' : Math.max(1, Math.trunc(+e.target.value)))}
                    style={inp} />
                </label>
                <label style={{ flex: 1, color: MUT, fontSize: 9 }}>PRICE (c)
                  <input type="number" min={1} max={99} value={cents} onChange={e => setCents(e.target.value === '' ? '' : Math.min(99, Math.max(1, Math.trunc(+e.target.value))))}
                    style={inp} />
                </label>
              </div>
              {pnl && (
                <div style={{ fontSize: 10, color: MUT, lineHeight: 1.6, marginBottom: 6 }}>
                  {pnl.label}: <span style={{ color: '#e0e0e0' }}>{money(pnl.principal)}</span> · max win <span style={{ color: GRN }}>{money(pnl.maxWin)}</span> · max loss <span style={{ color: RED }}>{money(pnl.maxLoss)}</span> · fee ~{money(pnl.fee)}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 10, marginBottom: 6 }}>
                <span style={{ color: MUT }}>YOUR PROB %</span>
                <input type="number" min={0} max={100} value={probEst}
                  onChange={e => setProbEst(e.target.value === '' ? '' : Math.min(100, Math.max(0, Math.round(+e.target.value))))}
                  placeholder="est." style={{ ...inp, width: 56, marginTop: 0 }} />
                {ev !== null && <span title="expected value of THIS ticket at your probability, net of fee" style={{ color: ev >= 0 ? GRN : RED, fontWeight: 'bold' }}>EV {money(ev)}{ev >= 0 ? ' +EV' : ''}</span>}
              </div>
              <button onClick={place} disabled={placing || !vaultOk} style={{
                width: '100%', background: action === 'buy' ? 'var(--accent)' : 'var(--loss)',
                color: action === 'buy' ? 'var(--accent-ink)' : '#fff', border: 'none',
                fontFamily: FONT, fontSize: 11, fontWeight: 'bold', padding: '7px 0', borderRadius: 2,
                opacity: (placing || !vaultOk) ? 0.45 : 1, cursor: (placing || !vaultOk) ? 'not-allowed' : 'pointer',
              }}>{placing ? '…' : `${action.toUpperCase()} ${side.toUpperCase()}${mode === 'demo' ? ' (DEMO)' : ''}`}</button>
            </div>
          </>
        ) : (
          <div style={{ color: MUT, fontSize: 10 }}>Select a market to see its book and trade.</div>
        )}

        {/* Portfolio */}
        {vaultOk && (
          <div style={{ borderTop: '1px solid #222', paddingTop: 8 }}>
            <div style={{ color: ORG, fontSize: 9, letterSpacing: 1.5, marginBottom: 4 }}>
              {venue === 'kalshi' ? 'KALSHI' : 'POLYMARKET'} ACCOUNT {mode === 'demo' ? '(DEMO)' : ''}
            </div>
            {pfQ.isError && <div style={{ color: RED, fontSize: 10 }}>Account unavailable — {(pfQ.error as Error)?.message}</div>}
            {pfQ.data && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 6 }}>
                  <span>Cash: <span style={{ color: '#e0e0e0' }}>{money(num(pfQ.data.balance?.cashDollars))}</span></span>
                  {Number.isFinite(num(pfQ.data.equityDollars)) && (
                    <span title="cash + mark-to-market value of open positions">Equity: <span style={{ color: ORG }}>{money(num(pfQ.data.equityDollars))}</span></span>
                  )}
                </div>
                <div style={{ color: MUT, fontSize: 9, letterSpacing: 1, marginBottom: 2 }}>POSITIONS</div>
                {(pfQ.data.positions ?? []).length === 0 && <div style={{ color: MUT, fontSize: 10 }}>none</div>}
                {(pfQ.data.positions ?? []).map((p, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#e0e0e0' }}>
                    <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.ticker}</span>
                    <span>
                      <span style={{ color: p.side === 'yes' ? GRN : RED }}>{String(p.side ?? '').toUpperCase()} {p.count}</span>
                      {Number.isFinite(num(p.marketValue)) && <span style={{ color: MUT }}> · {money(num(p.marketValue))}</span>}
                    </span>
                  </div>
                ))}
                <div style={{ color: MUT, fontSize: 9, letterSpacing: 1, margin: '6px 0 2px' }}>RESTING ORDERS</div>
                {(pfQ.data.orders ?? []).length === 0 && <div style={{ color: MUT, fontSize: 10 }}>none</div>}
                {(pfQ.data.orders ?? []).map(o => (
                  <div key={o.orderId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, gap: 6 }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#e0e0e0' }}>{o.ticker} {String(o.side ?? '').toUpperCase()} {o.remainingCount}/{o.count} @ {pct(o.price)}</span>
                    <button onClick={() => cancel(o.orderId)} style={{ background: 'transparent', color: RED, border: `1px solid ${RED}44`, fontFamily: FONT, fontSize: 9, padding: '1px 6px', borderRadius: 2, cursor: 'pointer' }}>×</button>
                  </div>
                ))}
                {/* Live fills blotter — streamed straight from the relay. */}
                {stream.fills.length > 0 && (
                  <>
                    <div style={{ color: MUT, fontSize: 9, letterSpacing: 1, margin: '6px 0 2px' }}>
                      RECENT FILLS <span style={{ color: GRN }}>● live</span>
                    </div>
                    {stream.fills.slice(0, 8).map((f, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#cfcfcf' }}>
                        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.ticker}</span>
                        <span>
                          <span style={{ color: f.side === 'yes' ? GRN : RED }}>{String(f.action ?? '').toUpperCase()} {String(f.side ?? '').toUpperCase()} {f.count}</span>
                          {f.price != null ? ` @ ${pct(f.price)}` : ''}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const inp: React.CSSProperties = {
  width: '100%', background: '#111', color: '#ddd', border: '1px solid #333',
  fontFamily: FONT, fontSize: 11, padding: '3px 6px', borderRadius: 2, marginTop: 2,
};
const selStyle: React.CSSProperties = {
  background: '#111', color: '#bbb', border: '1px solid #333',
  fontFamily: FONT, fontSize: 10, padding: '2px 4px', borderRadius: 2, cursor: 'pointer',
};

// Human time-to-close from an ISO string. `soon` is within 24h (or closed).
function timeToClose(iso: string | null | undefined): { label: string; soon: boolean } {
  if (!iso) return { label: '—', soon: false };
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return { label: '—', soon: false };
  const ms = t - Date.now();
  if (ms <= 0) return { label: 'closed', soon: true };
  const h = ms / 3.6e6;
  if (h < 1) return { label: `${Math.max(1, Math.round(ms / 6e4))}m`, soon: true };
  if (h < 48) return { label: `${Math.round(h)}h`, soon: h < 24 };
  return { label: `${Math.round(h / 24)}d`, soon: false };
}

// ── Probability trajectory (self-contained SVG; no chart lib) ──────────────
// Plots the YES price (implied probability) over time. Colored by direction vs
// the first point. Renders an honest empty state rather than a flat line when
// there is no history.
function ProbChart({ points, live, loading }: { points: HistoryPoint[]; live: number | null; loading?: boolean }) {
  const W = 296, H = 92, PAD = 5;
  if (!points || points.length < 2) {
    return (
      <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUT, fontSize: 10, border: '1px solid #1a1a1a', borderRadius: 3 }}>
        {loading ? 'Loading history…' : points && points.length === 1 ? 'Not enough history yet' : 'No trade history'}
      </div>
    );
  }
  const ys = points.map(p => p.yesPrice);
  const lo = Math.min(...ys), hi = Math.max(...ys);
  const span = Math.max(1e-6, hi - lo);
  const x = (i: number) => PAD + (i / (points.length - 1)) * (W - 2 * PAD);
  const y = (v: number) => PAD + (1 - (v - lo) / span) * (H - 2 * PAD);
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.yesPrice).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${(H - PAD).toFixed(1)} L${x(0).toFixed(1)},${(H - PAD).toFixed(1)} Z`;
  const lastV = live != null && Number.isFinite(live) ? live : ys[ys.length - 1];
  const col = lastV >= ys[0] ? '#3fbf6f' : '#e5484d';
  return (
    <div style={{ border: '1px solid #1a1a1a', borderRadius: 3, padding: '4px 6px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 1 }}>
        <span style={{ fontSize: 9, color: MUT, letterSpacing: 1 }}>IMPLIED PROB</span>
        <span style={{ fontSize: 15, fontWeight: 'bold', color: col }}>{(lastV * 100).toFixed(0)}%</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
        <path d={area} fill={col} opacity={0.10} />
        <path d={line} fill="none" stroke={col} strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8.5, color: MUT }}>
        <span>lo {(lo * 100).toFixed(0)}%</span><span>{points.length} trades</span><span>hi {(hi * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
}

// ── Depth-weighted order book ──────────────────────────────────────────────
// Each level carries a background bar sized by cumulative depth, so the shape of
// resting liquidity is visible at a glance. YES bars grow from the left, NO from
// the right, meeting at the spread.
function DepthBook({ book }: { book: Orderbook | null }) {
  const sides: Side[] = ['yes', 'no'];
  const totalOf = (s: Side) => (book?.[s] ?? []).slice(0, 6).reduce((a, l) => a + (Number.isFinite(l.count) ? l.count : 0), 0);
  const maxCum = Math.max(1, totalOf('yes'), totalOf('no'));
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {sides.map(s => {
        const levels = (book?.[s] ?? []).slice(0, 6);
        let cum = 0;
        return (
          <div key={s} style={{ flex: 1 }}>
            <div style={{ color: s === 'yes' ? GRN : RED, fontSize: 9, letterSpacing: 1, marginBottom: 2 }}>{s.toUpperCase()} book</div>
            {levels.map((l, i) => {
              cum += l.count;
              const w = Math.min(100, (cum / maxCum) * 100);
              return (
                <div key={i} style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#cfcfcf', fontFamily: 'Consolas, monospace', padding: '0 2px', lineHeight: 1.55 }}>
                  <div style={{ position: 'absolute', top: 0, bottom: 0, width: `${w}%`, ...(s === 'yes' ? { left: 0 } : { right: 0 }), background: s === 'yes' ? 'rgba(63,191,111,0.16)' : 'rgba(229,72,77,0.16)' }} />
                  <span style={{ position: 'relative' }}>{pct(l.price)}</span>
                  <span style={{ position: 'relative' }}>{l.count}</span>
                </div>
              );
            })}
            {levels.length === 0 && <div style={{ color: MUT, fontSize: 9 }}>—</div>}
          </div>
        );
      })}
    </div>
  );
}
